
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { GOLD_PURITIES, SILVER_PURITIES, DEFAULT_GOLD_PURITY, DEFAULT_SILVER_PURITY } from '@/lib/constants';
import { debugLog } from '@/utils/debugLogger';

// Re-export types
export * from '@/types/user';
export * from '@/types/dealer';
export * from '@/types/price';

// Import slices
import { PriceState, createPriceSlice } from './slices/price';
import { DealerState, createDealerSlice } from './slices/dealer';
import { UserState, createUserSlice } from './slices/auth/userSlice';
import { DebugState, createDebugSlice } from './slices/debug/debugSlice';
import { GeolocationState, createGeolocationSlice } from './slices/geolocation';

// Create the combined store type
export type StoreState = PriceState & DealerState & UserState & DebugState & GeolocationState;

// Initialize SSR data immediately during store creation
const initializeSSRData = () => {
  if (typeof window === 'undefined') return {};
  
  const homePageData = (window as any).__HOME_PAGE_DATA__;
  const metalPrices = (window as any).__METAL_PRICES__;
  
  debugLog('general', '[Store] Checking SSR data', { 
    hasHomePageData: !!homePageData,
    hasMetalPrices: !!metalPrices,
    goldPrice: metalPrices?.gold?.price ?? metalPrices?.gold?.marketPrice ?? homePageData?.metalPrices?.gold?.price,
    silverPrice: metalPrices?.silver?.price ?? metalPrices?.silver?.marketPrice ?? homePageData?.metalPrices?.silver?.price
  }, 'useStore');
  
  const updates: any = {};
  
  // Use metal prices data if available (more detailed)
  // Support both spot-standard shape ({ price }) and dealer-specific shape ({ marketPrice })
  if (metalPrices?.gold || metalPrices?.silver) {
    if (metalPrices.gold) {
      const goldPrice = metalPrices.gold.price ?? metalPrices.gold.marketPrice;
      if (typeof goldPrice === 'number') {
        updates.currentGoldPrice = {
          price: goldPrice,
          change: metalPrices.gold.change || 0,
          changePercentage: metalPrices.gold.changePercentage || 0,
          timestamp: metalPrices.gold.timestamp || ''
        };
        updates.rawGoldPrice = updates.currentGoldPrice;
        updates.goldPrice = goldPrice;
        debugLog('general', '[Store] Setting gold price from SSR metal prices', goldPrice, 'useStore');
      }
    }
    
    if (metalPrices.silver) {
      const silverPrice = metalPrices.silver.price ?? metalPrices.silver.marketPrice;
      if (typeof silverPrice === 'number') {
        updates.currentSilverPrice = {
          price: silverPrice,
          change: metalPrices.silver.change || 0,
          changePercentage: metalPrices.silver.changePercentage || 0,
          timestamp: metalPrices.silver.timestamp || ''
        };
        updates.rawSilverPrice = updates.currentSilverPrice;
        updates.silverPrice = silverPrice;
        debugLog('general', '[Store] Setting silver price from SSR metal prices', silverPrice, 'useStore');
      }
    }
  }
  // Fallback to home page data
  else if (homePageData?.metalPrices) {
    if (homePageData.metalPrices.gold) {
      updates.currentGoldPrice = {
        price: homePageData.metalPrices.gold.price,
        change: 0,
        changePercentage: 0,
        timestamp: homePageData.metalPrices.gold.timestamp || ''
      };
      updates.rawGoldPrice = updates.currentGoldPrice;
      updates.goldPrice = homePageData.metalPrices.gold.price;
      debugLog('general', '[Store] Setting gold price from SSR home page data', homePageData.metalPrices.gold.price, 'useStore');
    }
    
    if (homePageData.metalPrices.silver) {
      updates.currentSilverPrice = {
        price: homePageData.metalPrices.silver.price,
        change: 0,
        changePercentage: 0,
        timestamp: homePageData.metalPrices.silver.timestamp || ''
      };
      updates.rawSilverPrice = updates.currentSilverPrice;
      updates.silverPrice = homePageData.metalPrices.silver.price;
      debugLog('general', '[Store] Setting silver price from SSR home page data', homePageData.metalPrices.silver.price, 'useStore');
    }
  }
  
  // Set loading to false since we have data
  if (Object.keys(updates).length > 0) {
    updates.isPriceLoading = false;
    updates.error = null;
    debugLog('general', '[Store] Successfully initialized with SSR data', updates, 'useStore');
  } else {
    debugLog('general', '[Store] No SSR data found to initialize', undefined, 'useStore');
  }
  
  return updates;
};

// Create the store with all slices and immediate SSR initialization
export const useStore = create<StoreState>()(
  persist(
    (...args) => {
      const ssrData = initializeSSRData();
      return {
        ...createPriceSlice(...args),
        ...createDealerSlice(...args),
        ...createUserSlice(...args),
        ...createDebugSlice(...args),
        ...createGeolocationSlice(...args),
        // Override with SSR data if available
        ...ssrData,
      };
    },
    {
      name: 'gildy-store',
      version: 2, // Increment version to trigger migration and fix purity values
      skipHydration: true, // Prevent localStorage from overwriting SSR defaults during hydration
      storage: createJSONStorage(() => {
        if (typeof window === 'undefined') {
          // SSR-safe noop storage: prevents localStorage access during server render
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {},
          };
        }
        return localStorage;
      }),
      migrate: (persistedState: any, version: number) => {
        if (version < 2) {
          // Force reset delle purità ai valori corretti
          persistedState.goldPurity = DEFAULT_GOLD_PURITY; // "24 KT"
          persistedState.silverPurity = DEFAULT_SILVER_PURITY; // "PURO"
        }
        
        // Verifica sempre che i valori delle purità siano validi
        const validGoldPurities = Object.keys(GOLD_PURITIES);
        if (!persistedState.goldPurity || !validGoldPurities.includes(persistedState.goldPurity)) {
          debugLog('general', 'Invalid gold purity, resetting to default:', persistedState.goldPurity, 'Store Migration');
          persistedState.goldPurity = DEFAULT_GOLD_PURITY;
        }

        const validSilverPurities = Object.keys(SILVER_PURITIES);
        if (!persistedState.silverPurity || !validSilverPurities.includes(persistedState.silverPurity)) {
          debugLog('general', 'Invalid silver purity, resetting to default:', persistedState.silverPurity, 'Store Migration');
          persistedState.silverPurity = DEFAULT_SILVER_PURITY;
        }
        
        debugLog('general', 'Final purity values:', {
          goldPurity: persistedState.goldPurity,
          silverPurity: persistedState.silverPurity
        }, 'Store Migration');
        
        return persistedState;
      },
      // 🛡️ Merge-safe: non permettere che lo storage sovrascriva i prezzi SSR con 0/null post-hydration
      merge: (persistedState: any, currentState: any) => {
        const merged = { ...currentState, ...(persistedState || {}) };

        // Se SSR ha già prezzi validi, mantienili
        const preserveKeys = [
          'currentGoldPrice',
          'currentSilverPrice',
          'rawGoldPrice',
          'rawSilverPrice',
          'goldPrice',
          'silverPrice',
          'isPriceLoading',
          'error'
        ];

        for (const key of preserveKeys) {
          const cur = currentState?.[key];
          const isValidPriceNumber = typeof cur === 'number' ? cur > 0 : true;
          const shouldPreserve = cur !== null && cur !== undefined && isValidPriceNumber;

          if (shouldPreserve) {
            merged[key] = cur;
          }
        }

        return merged;
      },
      partialize: (state) => ({
        // User preferences
        favoritesDealers: state.favoritesDealers,
        selectedTimePeriod: state.selectedTimePeriod,
        // priceAlerts NON persistiti - devono essere caricati freschi dal database per evitare flickering
        goldPurity: state.goldPurity,
        silverPurity: state.silverPurity,
        silverUnit: state.silverUnit,
        // Keep user session
        user: state.user,
        // Debug settings
        debugEnabled: state.debugEnabled,
        debugAuth: state.debugAuth,
        debugAPI: state.debugAPI,
        debugBlog: state.debugBlog,
        debugDealer: state.debugDealer,
        debugCache: state.debugCache,
        debugPricing: state.debugPricing,
        // Geolocation state
        position: state.position,
        coordinates: state.coordinates,
        lastUpdated: state.lastUpdated,

        // NOTE: prezzi NON persistiti: devono venire da SSR/polling per evitare flash a 0
      }),
    }
  )
);
